import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import io from 'socket.io-client';
import MetaTags from '../components/MetaTags';
import { getSocketUrl } from '../utils/urlHelper';
import {
  DEFAULT_LIVE_TEXT_SETTINGS,
  normalizeLiveTextSettings,
  hasLiveTextContent,
  isTransparentQueryValue,
  clampBackgroundOpacity,
  hexToRgba,
  overlayTextShadow,
  defaultTextShadow
} from '../utils/liveTextSettings';

const LiveTextView = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const urlForcesTransparent = isTransparentQueryValue(searchParams.get('transparent'));
  const urlOpacityParam = searchParams.get('opacity');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [liveTextSettings, setLiveTextSettings] = useState(DEFAULT_LIVE_TEXT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const previousTextRef = useRef('');

  const savedOpacity = clampBackgroundOpacity(liveTextSettings.backgroundOpacity, 100);
  const backgroundOpacity = urlForcesTransparent
    ? 0
    : (urlOpacityParam != null && urlOpacityParam !== ''
      ? clampBackgroundOpacity(urlOpacityParam, savedOpacity)
      : savedOpacity);
  const hasContent = hasLiveTextContent(liveTextSettings.text);
  const needsOverlayReadability = backgroundOpacity <= 70;
  const pageBackground = hasContent
    ? hexToRgba(liveTextSettings.backgroundColor, backgroundOpacity)
    : 'transparent';
  const textShadow = needsOverlayReadability ? overlayTextShadow : defaultTextShadow;

  // Initialize socket connection
  useEffect(() => {
    const socketUrl = getSocketUrl();
    const newSocket = io(socketUrl, {
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      transports: ['websocket', 'polling']
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('Live Text - Socket connection error:', error);
    });
    
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Join session room and listen for updates
  useEffect(() => {
    if (socket && sessionId) {
      socket.emit('join-room', sessionId);
      socket.emit('join-room', `session-${sessionId}`);

      // Listen for live text settings updates
      socket.on('live-text-settings-updated', (data) => {
        if (String(data.sessionId) === String(sessionId)) {
          console.log('Live text settings updated via socket:', data.settings);
          const nextSettings = normalizeLiveTextSettings(data.settings);
          
          // Check if the text changed (not just styling)
          if (nextSettings.text !== previousTextRef.current) {
            previousTextRef.current = nextSettings.text;
            // Flash only when new visible text arrives, not when the overlay is cleared
            if (hasLiveTextContent(nextSettings.text)) {
              setIsFlashing(true);
              setTimeout(() => {
                setIsFlashing(false);
              }, 3000);
            } else {
              setIsFlashing(false);
            }
          }
          
          setLiveTextSettings(nextSettings);
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('live-text-settings-updated');
      }
    };
  }, [socket, sessionId]);

  // Fetch session info and initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch session info
        const sessionResponse = await api.get(`/api/sessions/${sessionId}`);
        const session = sessionResponse.data.session;
        setSessionInfo(session);

        // Fetch live text settings
        const settingsResponse = await api.get(`/api/sessions/${sessionId}/live-text-settings`);
        if (settingsResponse.data.settings) {
          const loadedSettings = normalizeLiveTextSettings(settingsResponse.data.settings);
          setLiveTextSettings(loadedSettings);
          previousTextRef.current = loadedSettings.text;
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId]);

  // Keep html/body/#root transparent so only the background layer carries color/opacity.
  // This is required for OBS/vMix browser sources to key the overlay correctly.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const previous = {
      htmlBg: html.style.background,
      htmlBgColor: html.style.backgroundColor,
      bodyBg: body.style.background,
      bodyBgColor: body.style.backgroundColor,
      rootBg: root ? root.style.background : '',
      rootBgColor: root ? root.style.backgroundColor : ''
    };

    [html, body, root].forEach((el) => {
      if (!el) return;
      el.style.background = 'transparent';
      el.style.backgroundColor = 'transparent';
    });

    return () => {
      html.style.background = previous.htmlBg;
      html.style.backgroundColor = previous.htmlBgColor;
      body.style.background = previous.bodyBg;
      body.style.backgroundColor = previous.bodyBgColor;
      if (root) {
        root.style.background = previous.rootBg;
        root.style.backgroundColor = previous.rootBgColor;
      }
    };
  }, []);

  const overlayChrome = (
    <style>{`
      html, body, #root, .live-text-view, .live-text-content {
        background: transparent !important;
        background-color: transparent !important;
      }
      .live-text-view {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }
      .live-text-background {
        position: absolute;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        background-color: ${hasContent ? pageBackground : 'transparent'};
      }
      .live-text-content {
        position: relative;
        z-index: 1;
        min-height: 100%;
        width: 100%;
      }
      @keyframes flashColors {
        0% {
          color: ${liveTextSettings.textColor};
          text-shadow: ${textShadow};
        }
        50% {
          color: #FFD700;
          text-shadow: ${textShadow};
        }
        100% {
          color: ${liveTextSettings.textColor};
          text-shadow: ${textShadow};
        }
      }
      .flash-text {
        animation: flashColors 0.6s ease-in-out;
      }
    `}</style>
  );

  const textStyle = {
    color: liveTextSettings.textColor,
    fontSize: `${liveTextSettings.fontSize}px`,
    fontWeight: 'bold',
    lineHeight: '1.3',
    textShadow,
    WebkitTextStroke: needsOverlayReadability ? '0.4px rgba(0, 0, 0, 0.55)' : '0',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    hyphens: 'auto',
    whiteSpace: 'pre-line',
    opacity: 1
  };

  if (loading) {
    return (
      <div className="live-text-view">
        <MetaTags 
          title="Loading Live Text... - AskTide"
          description="Live text display for broadcast integration"
          url={window.location.href}
        />
        {overlayChrome}
      </div>
    );
  }

  if (!sessionInfo) {
    return (
      <div className="live-text-view">
        <MetaTags 
          title="Session Not Found - AskTide"
          description="Live text display for broadcast integration"
          url={window.location.href}
        />
        <div className="live-text-background" aria-hidden="true" />
        <div className="live-text-content min-h-screen flex items-center justify-center">
          <div className="text-xl" style={textStyle}>Session not found</div>
        </div>
        {overlayChrome}
      </div>
    );
  }

  return (
    <div className="live-text-view">
      <MetaTags 
        title={`Live Text - ${sessionInfo.name || 'Live Session'} - AskTide`}
        description="Live text display for broadcast integration"
        url={window.location.href}
      />
      {hasContent && <div className="live-text-background" aria-hidden="true" />}
      {hasContent && (
        <div 
          className={`live-text-content min-h-screen flex justify-center overflow-hidden ${
            liveTextSettings.verticalPosition === 'top' ? 'items-start pt-12' :
            liveTextSettings.verticalPosition === 'bottom' ? 'items-end pb-12' :
            'items-center'
          }`}
          style={{
            paddingLeft: `${liveTextSettings.margin}%`,
            paddingRight: `${liveTextSettings.margin}%`
          }}
        >
          <div 
            className={`text-center break-words max-w-full ${isFlashing ? 'flash-text' : ''}`}
            style={textStyle}
          >
            {liveTextSettings.text}
          </div>
        </div>
      )}
      {overlayChrome}
    </div>
  );
};

export default LiveTextView;
